import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Spinner, Row, Col } from 'react-bootstrap';
import { addEvent, editEvent } from '../../../api/events';
import './EventModal.scss';

const formatDateToLocalString = (date) => {
  if (!date || isNaN(date.getTime())) return '';
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const formatTimeToLocalString = (date) => {
  if (!date || isNaN(date.getTime())) return '';
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
};

export const EventModal = ({ show, onHide, communityId, eventToEdit, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    locationName: '',
    eventType: 'free',
    fee: '',
    date: '',
    endDate: '',
    startTime: '',
    endTime: '',
    is_recurrence: false,
    repeatType: 'weekly',
    repeatInterval: 1,
    repeatOn: [],
    repeatEndType: 'never',
    repeatEndDate: '',
    repeatEndAfterOccurrences: '',
    access: 'public'
  });
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (eventToEdit) {
      const startStr = eventToEdit.startDate || eventToEdit.start_date || eventToEdit.date;
      const endStr = eventToEdit.endDate || eventToEdit.end_date;

      let dateVal = '';
      let endDateVal = '';
      let startTimeVal = '';
      let endTimeVal = '';

      if (startStr) {
        const parsedStart = new Date(startStr);
        if (!isNaN(parsedStart.getTime())) {
          dateVal = formatDateToLocalString(parsedStart);
          startTimeVal = formatTimeToLocalString(parsedStart);
        } else if (typeof startStr === 'string') {
          dateVal = startStr.split('T')[0];
        }
      }

      if (endStr) {
        const parsedEnd = new Date(endStr);
        if (!isNaN(parsedEnd.getTime())) {
          endDateVal = formatDateToLocalString(parsedEnd);
          endTimeVal = formatTimeToLocalString(parsedEnd);
        } else if (typeof endStr === 'string') {
          endDateVal = endStr.split('T')[0];
        }
      }

      // Parse repeatOn weekly days
      let repeatOnVal = [];
      if (eventToEdit.repeatOn) {
        if (Array.isArray(eventToEdit.repeatOn)) {
          repeatOnVal = eventToEdit.repeatOn;
        } else if (typeof eventToEdit.repeatOn === 'string') {
          try {
            repeatOnVal = JSON.parse(eventToEdit.repeatOn);
          } catch {
            repeatOnVal = eventToEdit.repeatOn.split(/[,| ]/).map(x => x.trim()).filter(Boolean);
          }
        }
      }

      setFormData({
        title: eventToEdit.title || '',
        description: eventToEdit.description || '',
        locationName: eventToEdit.locationName || '',
        eventType: eventToEdit.eventType || 'free',
        fee: eventToEdit.class_fees || eventToEdit.cost || eventToEdit.fee || '',
        date: dateVal,
        endDate: endDateVal || dateVal,
        startTime: startTimeVal || eventToEdit.start_time || '',
        endTime: endTimeVal || eventToEdit.end_time || '',
        is_recurrence: !!(eventToEdit.is_recurrence || eventToEdit.isRecurrence),
        repeatType: eventToEdit.repeatType || 'weekly',
        repeatInterval: eventToEdit.repeatInterval || 1,
        repeatOn: repeatOnVal,
        repeatEndType: eventToEdit.repeatEndType || 'never',
        repeatEndDate: eventToEdit.repeatEndDate ? formatDateToLocalString(new Date(eventToEdit.repeatEndDate)) : '',
        repeatEndAfterOccurrences: eventToEdit.repeatEndAfterOccurrences || '',
        access: eventToEdit.access || 'public'
      });

      if (eventToEdit.coverImage || eventToEdit.cover_image) {
        setCoverPreview(eventToEdit.coverImage || eventToEdit.cover_image);
      } else {
        setCoverPreview(null);
      }
      setCoverFile(null);
    } else {
      setFormData({
        title: '',
        description: '',
        locationName: '',
        eventType: 'free',
        fee: '',
        date: '',
        endDate: '',
        startTime: '',
        endTime: '',
        is_recurrence: false,
        repeatType: 'weekly',
        repeatInterval: 1,
        repeatOn: [],
        repeatEndType: 'never',
        repeatEndDate: '',
        repeatEndAfterOccurrences: '',
        access: 'public'
      });
      setCoverPreview(null);
      setCoverFile(null);
    }
    setError('');
  }, [eventToEdit, show]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCoverFile(file);
      setCoverPreview(URL.createObjectURL(file));
    }
  };

  const handleDayToggle = (day) => {
    setFormData(prev => {
      const exists = prev.repeatOn.includes(day);
      const newDays = exists
        ? prev.repeatOn.filter(d => d !== day)
        : [...prev.repeatOn, day];
      return { ...prev, repeatOn: newDays };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      let startDateUtc = '';
      let endDateUtc = '';
      if (formData.date && formData.startTime) {
        startDateUtc = new Date(`${formData.date}T${formData.startTime}`).toISOString();
      }
      if (formData.endDate && formData.endTime) {
        endDateUtc = new Date(`${formData.endDate}T${formData.endTime}`).toISOString();
      } else if (formData.date && formData.endTime) {
        endDateUtc = new Date(`${formData.date}T${formData.endTime}`).toISOString();
      }

      const formPayload = new FormData();
      formPayload.append('title', formData.title);
      formPayload.append('description', formData.description);
      formPayload.append('locationName', formData.locationName);
      formPayload.append('eventType', formData.eventType);
      formPayload.append('fee', formData.eventType === 'free' ? '0' : String(formData.fee));
      formPayload.append('date', startDateUtc || formData.date);
      formPayload.append('startDate', startDateUtc);
      formPayload.append('endDate', endDateUtc);
      formPayload.append('start_date', startDateUtc);
      formPayload.append('end_date', endDateUtc);
      formPayload.append('startTime', formData.startTime);
      formPayload.append('endTime', formData.endTime);
      formPayload.append('access', formData.access);
      formPayload.append('community', communityId);
      
      formPayload.append('is_recurrence', String(formData.is_recurrence));
      formPayload.append('repeatType', formData.is_recurrence ? formData.repeatType : 'onetime');
      
      if (formData.is_recurrence) {
        formPayload.append('repeatInterval', String(formData.repeatInterval));
        formPayload.append('repeatOn', JSON.stringify(formData.repeatOn));
        formPayload.append('repeatEndType', formData.repeatEndType);
        if (formData.repeatEndType === 'onDate' && formData.repeatEndDate) {
          formPayload.append('repeatEndDate', new Date(formData.repeatEndDate).toISOString());
        } else if (formData.repeatEndType === 'afterOccurrences' && formData.repeatEndAfterOccurrences) {
          formPayload.append('repeatEndAfterOccurrences', String(formData.repeatEndAfterOccurrences));
        }
      }

      if (coverFile) {
        formPayload.append('coverImage', coverFile);
      }

      if (eventToEdit) {
        await editEvent(eventToEdit._id, formPayload);
      } else {
        await addEvent(formPayload);
      }
      onSuccess();
      onHide();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || err.message || 'Failed to save class');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="md">
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="fw-bold w-100 text-center">{eventToEdit ? 'Edit Class' : 'Create Class'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body className="px-4">
          {error && <div className="alert alert-danger">{error}</div>}

          {/* Class Name */}
          <Form.Group className="mb-3">
            <Form.Label className="small text-muted fw-semibold mb-1">Class Name</Form.Label>
            <Form.Control 
              type="text" 
              name="title" 
              placeholder="Enter class name"
              value={formData.title} 
              onChange={handleChange} 
              required 
              className="cd-input-gray border-0"
            />
          </Form.Group>

          {/* Class Description */}
          <Form.Group className="mb-3">
            <Form.Label className="small text-muted fw-semibold mb-1">Class Description</Form.Label>
            <Form.Control 
              as="textarea" 
              rows={3} 
              name="description" 
              placeholder="Enter class description"
              value={formData.description} 
              onChange={handleChange} 
              required 
              className="cd-input-gray border-0"
            />
          </Form.Group>

          {/* Upload Class Cover */}
          <Form.Group className="mb-3">
            <Form.Label className="small text-muted fw-semibold mb-1">Upload Class Cover</Form.Label>
            <div 
              className="cd-cover-upload-box d-flex flex-column align-items-center justify-content-center border rounded-4 p-4 position-relative overflow-hidden pointer"
              style={{ minHeight: '180px', cursor: 'pointer' }}
              onClick={() => document.getElementById('coverImageInput').click()}
            >
              {coverPreview ? (
                <img src={coverPreview} alt="Cover Preview" className="position-absolute w-100 h-100 object-fit-cover top-0 start-0" />
              ) : (
                <div className="text-center">
                  <i className="bi bi-image text-muted fs-1 mb-2"></i>
                  <span className="text-muted d-block small">Upload Class Cover</span>
                </div>
              )}
              <input 
                type="file" 
                id="coverImageInput" 
                accept="image/*" 
                className="d-none" 
                onChange={handleFileChange} 
              />
            </div>
          </Form.Group>

          {/* Class Location / Link */}
          <Form.Group className="mb-3">
            <Form.Label className="small text-muted fw-semibold mb-1">Class Location / Link</Form.Label>
            <Form.Control 
              type="text" 
              name="locationName" 
              placeholder="Enter location or link (Google Meet, Zoom, etc.)"
              value={formData.locationName}
              onChange={handleChange}
              className="cd-input-gray border-0"
            />
          </Form.Group>

          {/* Start Date & End Date */}
          <Row className="g-3 mb-3">
            <Col xs={6}>
              <Form.Group>
                <Form.Label className="small text-muted fw-semibold mb-1">Select Start Date</Form.Label>
                <div className="position-relative">
                  <Form.Control 
                    type="date" 
                    name="date" 
                    value={formData.date}
                    onChange={handleChange}
                    required
                    className="cd-input-gray border-0 pe-5"
                  />
                  <i className="bi bi-calendar3 position-absolute end-0 top-50 translate-middle-y me-3 text-muted"></i>
                </div>
              </Form.Group>
            </Col>
            <Col xs={6}>
              <Form.Group>
                <Form.Label className="small text-muted fw-semibold mb-1">Select End Date</Form.Label>
                <div className="position-relative">
                  <Form.Control 
                    type="date" 
                    name="endDate" 
                    value={formData.endDate}
                    onChange={handleChange}
                    required
                    className="cd-input-gray border-0 pe-5"
                  />
                  <i className="bi bi-calendar3 position-absolute end-0 top-50 translate-middle-y me-3 text-muted"></i>
                </div>
              </Form.Group>
            </Col>
          </Row>

          {/* Start Time & End Time */}
          <Row className="g-3 mb-3">
            <Col xs={6}>
              <Form.Group>
                <Form.Label className="small text-muted fw-semibold mb-1">Select Start Time</Form.Label>
                <div className="position-relative">
                  <Form.Control 
                    type="time" 
                    name="startTime" 
                    value={formData.startTime}
                    onChange={handleChange}
                    required
                    className="cd-input-gray border-0 pe-5"
                  />
                  <i className="bi bi-clock position-absolute end-0 top-50 translate-middle-y me-3 text-muted"></i>
                </div>
              </Form.Group>
            </Col>
            <Col xs={6}>
              <Form.Group>
                <Form.Label className="small text-muted fw-semibold mb-1">Select End Time</Form.Label>
                <div className="position-relative">
                  <Form.Control 
                    type="time" 
                    name="endTime" 
                    value={formData.endTime}
                    onChange={handleChange}
                    required
                    className="cd-input-gray border-0 pe-5"
                  />
                  <i className="bi bi-clock position-absolute end-0 top-50 translate-middle-y me-3 text-muted"></i>
                </div>
              </Form.Group>
            </Col>
          </Row>

          {/* Recurring Class Checkbox */}
          <Form.Group className="mb-3 d-flex justify-content-between align-items-center bg-light p-3 rounded-4 border border-light-subtle">
            <Form.Label className="mb-0 fw-semibold text-dark pointer" htmlFor="isRecurrenceCheckbox">Recurring Class</Form.Label>
            <Form.Check 
              type="checkbox" 
              id="isRecurrenceCheckbox"
              name="is_recurrence" 
              checked={formData.is_recurrence}
              onChange={handleChange}
              style={{ transform: 'scale(1.2)' }}
            />
          </Form.Group>

          {/* Recurrence Details */}
          {formData.is_recurrence && (
            <div className="p-3 bg-light rounded-4 mb-3 border border-light-subtle">
              <Form.Group className="mb-3">
                <Form.Label className="small text-muted fw-semibold mb-1">Repeat Pattern</Form.Label>
                <Form.Select 
                  name="repeatType" 
                  value={formData.repeatType}
                  onChange={handleChange}
                  className="cd-input-gray border-0"
                >
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="small text-muted fw-semibold mb-1">Repeat Every (Interval)</Form.Label>
                <Form.Control 
                  type="number" 
                  name="repeatInterval" 
                  min="1"
                  value={formData.repeatInterval}
                  onChange={handleChange}
                  className="cd-input-gray border-0"
                />
              </Form.Group>

              {formData.repeatType === 'weekly' && (
                <Form.Group className="mb-3">
                  <Form.Label className="small text-muted fw-semibold mb-1">Repeat On Days</Form.Label>
                  <div className="d-flex flex-wrap gap-2">
                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => {
                      const isSelected = formData.repeatOn.includes(day);
                      return (
                        <Button
                          key={day}
                          variant={isSelected ? 'primary' : 'outline-secondary'}
                          size="sm"
                          className="rounded-pill px-3 py-1"
                          onClick={() => handleDayToggle(day)}
                        >
                          {day}
                        </Button>
                      );
                    })}
                  </div>
                </Form.Group>
              )}

              <Form.Group className="mb-3">
                <Form.Label className="small text-muted fw-semibold mb-1">Ending Settings</Form.Label>
                <Form.Select 
                  name="repeatEndType" 
                  value={formData.repeatEndType}
                  onChange={handleChange}
                  className="cd-input-gray border-0"
                >
                  <option value="never">Never Ends</option>
                  <option value="onDate">Ends on Date</option>
                  <option value="afterOccurrences">Ends after occurrences</option>
                </Form.Select>
              </Form.Group>

              {formData.repeatEndType === 'onDate' && (
                <Form.Group className="mb-3">
                  <Form.Label className="small text-muted fw-semibold mb-1">End Date</Form.Label>
                  <Form.Control 
                    type="date" 
                    name="repeatEndDate" 
                    value={formData.repeatEndDate}
                    onChange={handleChange}
                    className="cd-input-gray border-0"
                  />
                </Form.Group>
              )}

              {formData.repeatEndType === 'afterOccurrences' && (
                <Form.Group className="mb-3">
                  <Form.Label className="small text-muted fw-semibold mb-1">Number of Occurrences</Form.Label>
                  <Form.Control 
                    type="number" 
                    name="repeatEndAfterOccurrences" 
                    min="1"
                    value={formData.repeatEndAfterOccurrences}
                    onChange={handleChange}
                    className="cd-input-gray border-0"
                  />
                </Form.Group>
              )}
            </div>
          )}

          {/* Class Type */}
          <Form.Group className="mb-4">
            <Form.Label className="small text-muted fw-semibold d-block mb-2">Class Type</Form.Label>
            <div className="d-flex flex-column gap-3">
              {/* Free Radio */}
              <div 
                className="d-flex align-items-start gap-3 pointer" 
                onClick={() => setFormData(prev => ({ ...prev, eventType: 'free' }))}
              >
                <Form.Check
                  type="radio"
                  name="eventType"
                  id="classTypeFree"
                  checked={formData.eventType === 'free'}
                  onChange={() => {}}
                  className="mt-1"
                  style={{ transform: 'scale(1.2)' }}
                />
                <label htmlFor="classTypeFree" className="pointer">
                  <div className="fw-semibold text-dark">Free to Join</div>
                  <small className="text-muted d-block" style={{ fontSize: '0.85rem' }}>Anyone can send a request to join.</small>
                </label>
              </div>

              {/* Paid Radio */}
              <div 
                className="d-flex align-items-start gap-3 pointer" 
                onClick={() => setFormData(prev => ({ ...prev, eventType: 'paid' }))}
              >
                <Form.Check
                  type="radio"
                  name="eventType"
                  id="classTypePaid"
                  checked={formData.eventType === 'paid'}
                  onChange={() => {}}
                  className="mt-1"
                  style={{ transform: 'scale(1.2)' }}
                />
                <label htmlFor="classTypePaid" className="pointer">
                  <div className="fw-semibold text-dark">Paid</div>
                  <small className="text-muted d-block" style={{ fontSize: '0.85rem' }}>Subscription required, per your settings.</small>
                </label>
              </div>
            </div>

            {formData.eventType === 'paid' && (
              <Form.Group className="mt-3">
                <Form.Label className="small text-muted fw-semibold mb-1">Fee ($)</Form.Label>
                <Form.Control 
                  type="number" 
                  name="fee" 
                  placeholder="Enter fee amount"
                  value={formData.fee} 
                  onChange={handleChange} 
                  required 
                  min="1" 
                  className="cd-input-gray border-0"
                />
              </Form.Group>
            )}
          </Form.Group>

          {/* Class Visibility */}
          <Form.Group className="mb-4">
            <Form.Label className="small text-muted fw-semibold d-block mb-2">Class Visibility</Form.Label>
            <div className="d-flex flex-column gap-3">
              {/* Public Radio */}
              <div 
                className="d-flex align-items-start gap-3 pointer" 
                onClick={() => setFormData(prev => ({ ...prev, access: 'public' }))}
              >
                <Form.Check
                  type="radio"
                  name="access"
                  id="visibilityPublic"
                  checked={formData.access === 'public'}
                  onChange={() => {}}
                  className="mt-1"
                  style={{ transform: 'scale(1.2)' }}
                />
                <label htmlFor="visibilityPublic" className="pointer">
                  <div className="fw-semibold text-dark">Public</div>
                  <small className="text-muted d-block" style={{ fontSize: '0.85rem' }}>Anyone can join this class.</small>
                </label>
              </div>

              {/* Private Radio */}
              <div 
                className="d-flex align-items-start gap-3 pointer" 
                onClick={() => setFormData(prev => ({ ...prev, access: 'private' }))}
              >
                <Form.Check
                  type="radio"
                  name="access"
                  id="visibilityPrivate"
                  checked={formData.access === 'private'}
                  onChange={() => {}}
                  className="mt-1"
                  style={{ transform: 'scale(1.2)' }}
                />
                <label htmlFor="visibilityPrivate" className="pointer">
                  <div className="fw-semibold text-dark">Private</div>
                  <small className="text-muted d-block" style={{ fontSize: '0.85rem' }}>Only selected users can join this class.</small>
                </label>
              </div>
            </div>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="border-0 px-4 pb-4">
          <Button variant="secondary" onClick={onHide} disabled={loading} className="rounded-pill px-4">Cancel</Button>
          <Button variant="primary" type="submit" disabled={loading} className="rounded-pill px-4">
            {loading ? <Spinner size="sm" animation="border" /> : (eventToEdit ? 'Save Class' : 'Create Class')}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};
